import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Post from "../../../Components/Post/Post";
import ProfileHeader from "../../../Components/Profile/ProfileHeader";
import SubscriptionModal from "../../../Modals/SubscriptionModal";
import SignUpModal from "../../../Modals/SignupModal";
import { checkIsSubscribe, getToken, isCreatorRole } from "../../../Config/common.config";
import {
  getUserDetails,
} from "../../../Redux/slices/userSlice";
import { UserDetails } from "../../../Config/interface.config";
import { UserPublicProfile } from "./interface";
import * as H from "../../Home/style";
import { RootState, useAppDispatch } from "../../../Redux/store";
import { getUserDetailsByUsername } from "../../../Redux/slices/viewedProfileSlice";
import PostSkeleton from "../../../Components/Post/PostSkeleton";
import { useSelector } from "react-redux";
import CreatorOnboardingChecklist from "../../../Components/CreatorOnboardingChecklist";
import CreatePostModal from "../../../Modals/CreatePostModal";
import APIRequest from "../../../API";
import * as PATH from "../../../API/path";
import { getOrCreateFanxoVisitorId } from "../../../Utils/visitorIdentity";
import { usePullToRefresh } from "../../../hooks/usePullToRefresh";
const PROFILE_POST_LIMIT = 10;

const initialState: UserPublicProfile = {
  fullname: "",
  email: "",
  bio: "",
  role: "",
  isApproved: false,
  isDeleted: false,
  subscribeTo: [],
  createdAt: "",
  updatedAt: "",
  socialLinks: {
    username: "",
    instagram: "",
    youtube: "",
    threads: "",
    others: "",
  },
  username: "",
  image: { name: "", id: "", fileURL: "" },
  backgroundImage: { name: "", id: "", fileURL: "" },
  id: "",
  posts: [],
  roleType: "",
};

const ViewProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { username } = useParams();

  const [profile, setProfile] = useState<UserPublicProfile>(initialState);
  const [message, setMessage] = useState<string>("");
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openAuth, setOpenAuth] = useState<boolean>(false);
  const [createPostModal, setCreatePostModal] = useState(false);
  const [posts, setPosts] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const feedWrapperRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const trackedCreatorViewsRef = useRef<Set<string>>(new Set());

  const [refreshFeedsWithCreatorList, setRefreshFeedsWithCreatorList] =
    useState(false);

  const user = useSelector((state: RootState) => state.user);
  const isSubscribed = checkIsSubscribe(
    profile.id || "",
    user?.subscribeTo as any
  );
  const isOwnProfile = Boolean(user?.id && profile.id && user.id === profile.id);
  const approvedOwnCreator =
    isOwnProfile && isCreatorRole(profile.role) && profile.isApproved === true;
  const creatorPostCount = Math.max(
    Number(user?.totalPost ?? 0),
    profile.posts.length,
    posts.length
  );
  const showCreatorOnboarding = approvedOwnCreator && creatorPostCount === 0;
  const onboardingState = {
    profileComplete: Boolean(
      profile.image?.fileURL && profile.backgroundImage?.fileURL && profile.bio?.trim()
    ),
    membershipComplete: Boolean(
      profile.memberDetails?.monthlyPrice && profile.memberDetails?.messagePrice
    ),
    firstPostComplete: creatorPostCount > 0,
    shareComplete: Boolean(profile.username),
    username: profile.username,
  };

  const fetchUserDetails = async (username: string, page: number) => {
    if (page === 1) {
      setInitialLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await dispatch(
        getUserDetailsByUsername({ username, page, limit: PROFILE_POST_LIMIT })
      ).unwrap();

      if (!response || !response.id || !response.posts) {
        throw new Error("Invalid profile response");
      }

      const postResults = response?.posts?.results ?? [];
      const nextHasMore =
        typeof response.posts.hasMore === "boolean"
          ? response.posts.hasMore
          : Number(response.posts.page ?? page) < Number(response.posts.totalPages ?? page);

      if (page === 1) {
        setProfile(response);
        setPosts(postResults);

        const creatorId = response.id || response._id;
        if (creatorId && !trackedCreatorViewsRef.current.has(creatorId)) {
          trackedCreatorViewsRef.current.add(creatorId);
          APIRequest(PATH.trackCreatorProfileView, {
            creatorId,
            visitorId: getOrCreateFanxoVisitorId(),
          }).catch((error) => {
            console.error("Error tracking profile view:", error);
            trackedCreatorViewsRef.current.delete(creatorId);
          });
        }
      } else {
        setPosts((prev) => {
          const existingPostIds = new Set(prev.map((post) => post.id || post._id));
          const newPosts = postResults.filter(
            (post: any) => !existingPostIds.has(post.id || post._id)
          );

          return [...prev, ...newPosts];
        });
      }

      setHasMore(nextHasMore);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setInitialLoading(false);
      setLoadingMore(false);
    }
  };

  // useEffect(() => {
  //   const token = getToken();
  //   dispatch(viewProfilePage(!!token));
  // }, [dispatch]);

  useEffect(() => {
    if (username) {
      setPage(1);
      setHasMore(true);
      setPosts([]);
      fetchUserDetails(username, 1);
    }
  }, [username]);

  // Infinite scroll observer
  useEffect(() => {
    const root = feedWrapperRef.current;
    const observerElement = observerRef.current;
    if (!root || !observerElement || !hasMore || loadingMore || initialLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root,
        rootMargin: "0px 0px 300px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(observerElement);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, initialLoading]);

  useEffect(() => {
    if (page > 1 && username) {
      fetchUserDetails(username, page);
    }
  }, [page]);

  useEffect(() => {
    if (refreshFeedsWithCreatorList && username) {
      setPage(1);
      setHasMore(true);
      setPosts([]);
      fetchUserDetails(username, 1);
      setRefreshFeedsWithCreatorList(false);
      dispatch(getUserDetails());
    }
  }, [refreshFeedsWithCreatorList, username, dispatch]);
  const refreshProfile = async () => {
    if (!username || initialLoading || loadingMore) return;
    setPage(1);
    setHasMore(true);
    await fetchUserDetails(username, 1);
  };

  const profilePullToRefresh = usePullToRefresh(
    feedWrapperRef,
    refreshProfile,
    initialLoading || loadingMore,
  );

  const onSubscribe = (creator: Partial<UserDetails>) => {
    if (creator?.id) {
      setOpen(true);
    }
  };

  const onAuthUser = () => {
    setOpenAuth(true);
  };

  return (
    <H.HomeWrapper>
      <H.Feed className="feed">
        {message && (
          <div className="my-20">
            <p className="text-xl text-white text-center">{message}</p>
          </div>
        )}

        <H.FeedWrapper
          ref={feedWrapperRef}
          stretch={isCreatorRole(profile.role)}
          onTouchStart={profilePullToRefresh.onTouchStart}
          onTouchMove={profilePullToRefresh.onTouchMove}
          onTouchEnd={profilePullToRefresh.onTouchEnd}
          onTouchCancel={profilePullToRefresh.onTouchCancel}
        >
          <H.PullToRefreshIndicator
            $offset={profilePullToRefresh.pullDistance}
            $refreshing={profilePullToRefresh.refreshing}
          >
            {profilePullToRefresh.refreshing ? "Refreshing profile..." : profilePullToRefresh.readyToRefresh ? "Release to refresh" : "Pull down to refresh"}
          </H.PullToRefreshIndicator>
          {isCreatorRole(profile.role) && (
              <ProfileHeader
                isProfile={true}
                user={{
                  loading: initialLoading,
                  totalPost: profile.posts.length,
                  backgroundImage: profile.backgroundImage,
                  image: profile.image,
                  bio: profile.bio,
                  username: profile.username,
                  fullname: profile.fullname,
                  id: profile.id,
                  role: profile.role,
                  memberDetails: profile.memberDetails,
                  isApproved: profile.isApproved,
                  createdAt: profile.createdAt,
                  updatedAt: profile.updatedAt,
                  roleType: profile.roleType,
                }}
                subscribeCreator={onSubscribe}
                authenticateUser={onAuthUser}
                isSubscribed={isSubscribed}
              />
          )}

          <div className="feed-box">
              {initialLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <PostSkeleton key={`post-skeleton-initial-${i}`} />
                ))
              ) : posts.length > 0 ? (
                posts.map((post) => (
                  <Post
                    userImage={profile.image?.fileURL}
                    key={post.id || post._id}
                    post={post}
                    locked={!getToken() && post.isLocked}
                    isSubscribed={isOwnProfile || isSubscribed}
                  />
                ))
              ) : (
                <div className="text-white text-center mt-3 sm:mt-6">
                  {showCreatorOnboarding ? (
                    <CreatorOnboardingChecklist
                      state={onboardingState}
                      onCreatePost={() => setCreatePostModal(true)}
                    />
                  ) : (
                    <p>No posts to display.</p>
                  )}
                </div>
              )}

              {loadingMore &&
                Array.from({ length: 3 }).map((_, i) => (
                  <PostSkeleton key={`post-skeleton-${i}`} />
                ))}
            <div ref={observerRef} style={{ height: "50px" }} />
          </div>
        </H.FeedWrapper>
      </H.Feed>

      <SubscriptionModal
        selectedCreator={profile}
        onHide={() => setOpen(false)}
        show={open}
        setRefreshFeedsWithCreatorList={setRefreshFeedsWithCreatorList}
        urlusername={username}
      />

      <SignUpModal onHide={() => setOpenAuth(false)} show={openAuth} />
      <CreatePostModal
        onHide={() => setCreatePostModal(false)}
        show={createPostModal}
        key="profile-onboarding-create-post-modal"
      />
    </H.HomeWrapper>
  );
};

export default ViewProfile;
